import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import {
  brands,
  getBrandLabel,
  getBrandById,
  getCustomSelectedBrands,
  normalizeBrandInput,
  normalizeSelectedBrands
} from "../data/brands";

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--cb-text-muted)] mb-2">
      {children}
    </p>
  );
}

export default function BrandSelector({
  language = "en",
  selectedBrands = [],
  onChange
}) {
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customBrandName, setCustomBrandName] = useState("");

  const normalizedSelectedBrands = useMemo(
    () => normalizeSelectedBrands(selectedBrands),
    [selectedBrands]
  );

  const customBrands = useMemo(
    () => getCustomSelectedBrands(normalizedSelectedBrands),
    [normalizedSelectedBrands]
  );

  const visibleBrands = useMemo(
    () => brands.filter((brand) => !brand.legacy),
    []
  );

  const updateBrands = (nextBrands) => {
    onChange?.(normalizeSelectedBrands(nextBrands));
  };

  const toggleBrand = (brandId) => {
    const nextBrands = normalizedSelectedBrands.includes(brandId)
      ? normalizedSelectedBrands.filter((value) => value !== brandId)
      : [...normalizedSelectedBrands, brandId];

    updateBrands(nextBrands);
  };

  const handleAddCustomBrand = () => {
    const nextInput = normalizeBrandInput(customBrandName);
    if (!nextInput) return;

    const nextBrands = normalizeSelectedBrands([
      ...normalizedSelectedBrands,
      nextInput
    ]);

    updateBrands(nextBrands);
    setCustomBrandName("");
    setIsAddingCustom(false);
  };

  const actionLabel = language === "fr" ? "Autre / personnalise" : "Other / custom";
  const addLabel = language === "fr" ? "+ Ajouter une marque" : "+ Add a brand";
  const inputLabel = language === "fr" ? "Marque ou type de crayon" : "Brand or pencil type";
  const helperText = language === "fr"
    ? "Exemples : Arrtx, Posca, Sharpie, melange personnel"
    : "Examples: Arrtx, Posca, Sharpie, personal mix";
  const customSectionLabel = language === "fr" ? "Tes ajouts" : "Your custom picks";
  const addButtonText = language === "fr" ? "Ajouter" : "Add";
  const cancelButtonText = language === "fr" ? "Annuler" : "Cancel";

  const popularBrands = visibleBrands.filter((brand) => brand.popular);
  const standardBrands = visibleBrands.filter((brand) => !brand.popular && !brand.subtle && !brand.isFinishing);
  const subtleBrands = visibleBrands.filter((brand) => brand.subtle);
  const finishingBrands = visibleBrands.filter((brand) => brand.isFinishing);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <SectionLabel>{language === "fr" ? "Populaire" : "Popular"}</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {popularBrands.map((brand) => (
            <motion.button
              key={brand.id}
              onClick={() => toggleBrand(brand.id)}
              className={`px-5 py-3 rounded-2xl border-2 font-bold text-sm transition-colors ${
                normalizedSelectedBrands.includes(brand.id)
                  ? "bg-[color:var(--cb-primary)] text-white border-[color:var(--cb-primary)] shadow-[0_4px_12px_rgba(232,132,92,0.3)]"
                  : "bg-white text-[color:var(--cb-text)] border-[rgba(61,61,61,0.15)]"
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {getBrandLabel(brand.id, language)}
            </motion.button>
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>{language === "fr" ? "Autres marques" : "Other brands"}</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {standardBrands.map((brand) => (
            <motion.button
              key={brand.id}
              onClick={() => toggleBrand(brand.id)}
              className={`px-4 py-2 rounded-full border-2 font-semibold text-xs transition-colors ${
                normalizedSelectedBrands.includes(brand.id)
                  ? "bg-[color:var(--cb-primary)] text-white border-[color:var(--cb-primary)]"
                  : "bg-white text-[color:var(--cb-text)] border-[rgba(61,61,61,0.12)]"
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {getBrandLabel(brand.id, language)}
            </motion.button>
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>{language === "fr" ? "Basique" : "Basic"}</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {subtleBrands.map((brand) => (
            <motion.button
              key={brand.id}
              onClick={() => toggleBrand(brand.id)}
              className={`px-3 py-1.5 rounded-full border font-medium text-[11px] transition-colors ${
                normalizedSelectedBrands.includes(brand.id)
                  ? "bg-[color:var(--cb-primary)]/80 text-white border-[color:var(--cb-primary)]"
                  : "bg-[color:var(--cb-secondary)] text-[color:var(--cb-text-muted)] border-[rgba(61,61,61,0.08)]"
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {getBrandLabel(brand.id, language)}
            </motion.button>
          ))}
        </div>
      </div>

      <div className="rounded-[22px] border border-dashed border-[rgba(232,168,124,0.45)] bg-[rgba(255,250,246,0.82)] p-3">
        <motion.button
          type="button"
          onClick={() => setIsAddingCustom((prev) => !prev)}
          className="w-full flex items-center justify-center gap-2 rounded-full px-4 py-3 bg-white border border-[rgba(61,61,61,0.08)] text-sm font-semibold text-[color:var(--cb-text)]"
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} className="text-[color:var(--cb-primary)]" />
          {isAddingCustom ? actionLabel : addLabel}
        </motion.button>

        {isAddingCustom && (
          <div className="mt-3 space-y-3">
            <div className="rounded-[18px] bg-white border border-[rgba(61,61,61,0.08)] p-3">
              <label className="block text-xs font-semibold text-[color:var(--cb-text)] mb-2" htmlFor="custom-brand-input">
                {inputLabel}
              </label>
              <input
                id="custom-brand-input"
                type="text"
                value={customBrandName}
                onChange={(event) => setCustomBrandName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddCustomBrand();
                  }
                }}
                placeholder={language === "fr" ? "Ex. Arrtx ou Posca" : "Ex. Arrtx or Posca"}
                className="w-full rounded-2xl border border-[rgba(61,61,61,0.12)] bg-[rgba(255,255,255,0.96)] px-4 py-3 text-sm text-[color:var(--cb-text)] outline-none focus:border-[color:var(--cb-primary)]"
                maxLength={48}
              />
              <p className="mt-2 text-[11px] leading-relaxed text-[color:var(--cb-text-muted)]">
                {helperText}
              </p>
            </div>

            <div className="flex gap-2">
              <motion.button
                type="button"
                onClick={handleAddCustomBrand}
                disabled={!normalizeBrandInput(customBrandName)}
                className="flex-1 rounded-full bg-[color:var(--cb-primary)] px-4 py-2.5 text-sm font-bold text-white shadow-[0_8px_18px_rgba(232,168,124,0.25)] disabled:opacity-50"
                whileTap={{ scale: 0.98 }}
              >
                {addButtonText}
              </motion.button>
              <motion.button
                type="button"
                onClick={() => {
                  setCustomBrandName("");
                  setIsAddingCustom(false);
                }}
                className="rounded-full border border-[rgba(61,61,61,0.12)] bg-white px-4 py-2.5 text-sm font-semibold text-[color:var(--cb-text-muted)]"
                whileTap={{ scale: 0.98 }}
              >
                {cancelButtonText}
              </motion.button>
            </div>
          </div>
        )}

        {customBrands.length > 0 && (
          <div className="mt-3">
            <SectionLabel>{customSectionLabel}</SectionLabel>
            <div className="flex flex-wrap gap-2">
              {customBrands.map((brandId) => {
                const isLegacyBrand = Boolean(getBrandById(brandId)?.legacy);
                return (
                  <div
                    key={brandId}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold ${
                      isLegacyBrand
                        ? "bg-[rgba(255,255,255,0.88)] border-[rgba(61,61,61,0.12)] text-[color:var(--cb-text-muted)]"
                        : "bg-[rgba(232,168,124,0.12)] border-[rgba(232,168,124,0.28)] text-[color:var(--cb-text)]"
                    }`}
                  >
                    <span>{getBrandLabel(brandId, language)}</span>
                    <button
                      type="button"
                      onClick={() => toggleBrand(brandId)}
                      className="rounded-full p-0.5 text-[color:var(--cb-text-muted)] hover:text-[color:var(--cb-text)]"
                      aria-label={language === "fr" ? "Retirer la marque" : "Remove brand"}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-[rgba(61,61,61,0.08)]">
        <SectionLabel>{language === "fr" ? "Finition" : "Finishing"}</SectionLabel>
        {finishingBrands.map((brand) => (
          <motion.button
            key={brand.id}
            onClick={() => toggleBrand(brand.id)}
            className={`w-full py-3 rounded-2xl border-2 font-semibold text-sm flex items-center gap-3 transition-colors mb-2 ${
              normalizedSelectedBrands.includes(brand.id)
                ? "bg-[rgba(255,255,255,0.9)] text-[color:var(--cb-text)] border-[color:var(--cb-primary)] shadow-[0_2px_8px_rgba(232,132,92,0.15)]"
                : "bg-[rgba(255,255,255,0.5)] text-[color:var(--cb-text-muted)] border-[rgba(61,61,61,0.08)]"
            }`}
            whileTap={{ scale: 0.98 }}
          >
            <span className="text-lg">*</span>
            <span className="text-left">
              {getBrandLabel(brand.id, language)}
              <span className="block text-[10px] opacity-60 mt-0.5">
                {language === "fr" ? brand.typeFr : brand.type}
              </span>
            </span>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
